import { useState } from "react";

// v1.0.17 Project Register indicators — presentation only; states are derived server-side in the batched register response.
// Shared Register clip geometry (Pass 1A): every status indicator in the Register uses this box.
export const REG_CLIP = "inline-flex min-h-[34px] min-w-[96px] items-center justify-center whitespace-nowrap rounded-md border px-2.5 py-1 text-center text-[10px] font-semibold uppercase leading-tight";

function HoverTip({ tip, children, testId }) {
  const [pos, setPos] = useState(null);
  const show = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const left = Math.min(Math.max(8, r.left), window.innerWidth - 300);
    setPos(r.bottom + 190 > window.innerHeight ? { bottom: window.innerHeight - r.top + 6, left } : { top: r.bottom + 6, left });
  };
  return (
    <span className="relative inline-flex" onMouseEnter={show} onMouseLeave={() => setPos(null)} onClick={(e) => e.stopPropagation()}>
      {children}
      {pos && (
        <div role="tooltip" data-testid={testId} style={{ position: "fixed", zIndex: 60, ...pos }}
          className="w-[290px] rounded-md border border-gray-200 bg-white px-3 py-2.5 text-left shadow-[0_6px_20px_rgba(16,19,21,0.14)]">
          <div className="text-[10px] font-bold uppercase text-gray-500" style={{ letterSpacing: "0.1em" }}>{tip.title}</div>
          <p className="mt-1 text-xs leading-snug text-gray-800">{tip.text}</p>
          <ul className="mt-2 space-y-0.5 border-t border-gray-100 pt-2 text-[10px] text-gray-500">
            {tip.legend.map(([dot, label]) => (
              <li key={label} className="flex items-center gap-1.5"><span className={`h-2 w-2 shrink-0 rounded-full ${dot}`} />{label}</li>
            ))}
          </ul>
        </div>
      )}
    </span>
  );
}

const SUPPLY = {
  grey: { chip: "border-gray-200 bg-gray-50 text-gray-600", sub: "text-gray-400", text: "Distributor Application VQR Cost is Not Recorded" },
  orange: { chip: "border-orange-300 bg-orange-50 text-orange-700", sub: "text-orange-500/80", text: "Distributor Application VQR Cost is active and Pending" },
  red: { chip: "border-red-300 bg-red-50 text-red-700", sub: "text-red-500/80", text: "Distributor Application VQR Cost is active and Unpaid" },
  green: { chip: "border-emerald-300 bg-emerald-50 text-emerald-700", sub: "text-emerald-600/80", text: "Distributor Application VQR Cost is Paid (or Existing Asset — Completed by Others)" },
};
const SUPPLY_LEGEND = [["bg-gray-300", "Grey = Not Recorded"], ["bg-orange-500", "Orange = Pending"], ["bg-red-500", "Red = Unpaid"], ["bg-emerald-500", "Green = Paid / Existing Asset"]];

export function SupplyClip({ p }) {
  const s = SUPPLY[p.distributor_application_vqr_status] || SUPPLY.grey;
  return (
    <HoverTip testId={`ledger-supply-tip-${p.project_number}`} tip={{ title: "Supply Status", text: s.text, legend: SUPPLY_LEGEND }}>
      <span data-testid={`ledger-supply-${p.project_number}`} data-state={p.distributor_application_vqr_status || "grey"}
        className={`${REG_CLIP} flex-col normal-case !py-0.5 ${s.chip}`}>
        <span className="max-w-[150px] truncate text-[11px] font-semibold">{p.distributor || "—"}</span>
        <span className={`text-[10px] font-medium ${s.sub}`}>{p.supply_type}</span>
      </span>
    </HoverTip>
  );
}

const METERS = {
  red: { chip: "border-red-300 bg-red-50 text-red-700", faint: "text-red-400/80", desc: "CES not yet fully booked/completed across all required streams (including the Group Meter Panel CES)." },
  orange: { chip: "border-orange-300 bg-orange-50 text-orange-700", faint: "text-orange-400/80", desc: "All CES inspections are booked/completed. Awaiting all Distributor Submissions." },
  purple: { chip: "border-[#c9b8d9] bg-[#f3eff7] text-[#604184]", faint: "text-[#765297]/70", desc: "All Distributor Submissions submitted. Awaiting all Electrical Retailer submissions." },
  blue: { chip: "border-sky-300 bg-sky-50 text-sky-700", faint: "text-sky-400/80", desc: "All Electrical Retailers submitted. Awaiting final connections." },
  green: { chip: "border-emerald-300 bg-emerald-50 text-emerald-700", faint: "text-emerald-500/80", desc: "All required dwellings are connected." },
};
const METERS_LEGEND = [["bg-red-500", "Red = CES not fully booked/completed"], ["bg-orange-500", "Orange = CES ready; awaiting Distributor Submissions"],
  ["bg-[#765297]", "Purple = Distributor Submissions complete; awaiting Electrical Retailers"], ["bg-sky-500", "Blue = Electrical Retailers submitted; awaiting final connections"],
  ["bg-emerald-500", "Green = All required dwellings connected"]];

export function MetersClip({ p }) {
  const stage = p.meter_connection_stage || "red";
  const m = METERS[stage] || METERS.red;
  const req = p.dwellings_required ?? p.dwellings_total;
  const waived = (p.dwellings_waived || 0) > 0 ? ` ${p.dwellings_waived} stream(s) closed — future connection by others.` : "";
  return (
    <HoverTip testId={`ledger-meters-tip-${p.project_number}`} tip={{ title: "Meter Connection Status", text: m.desc + waived, legend: METERS_LEGEND }}>
      <span data-testid={`ledger-dwellings-${p.project_number}`} data-stage={stage} className={`${REG_CLIP} font-mono-data normal-case text-xs ${m.chip}`}>
        {p.dwellings_connected}<span className={`font-medium ${m.faint}`}>&nbsp;of {req}</span>
      </span>
    </HoverTip>
  );
}
